<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
});

const competition = ref(null);
const participant = ref(null);
const entries = ref([]);
const loading = ref(true);
const error = ref(null);

const PRO_MONTH_VALUE = 149;

const fetchCompetition = async () => {
    try {
        loading.value = true;
        const response = await axios.get(route('giveaway.participant.extend.data', props.uuid));
        competition.value = response.data.competition;
        participant.value = response.data.participant;
        entries.value = response.data.entries;
    } catch (error) {
        console.error('Error fetching competition:', error);
        error.value = 'Failed to load competition data. Please try again.';
    } finally {
        loading.value = false;
    }
};

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const formatEntryType = (type) => {
    switch (type) {
        case 'email':
            return 'Email Registration';
        case 'social_media_visit':
            return 'Social Media Visit';
        default:
            return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
};

const formatEntryValue = (entry) => {
    if (entry.entry_type === 'email') {
        return entry.entry_value;
    } else if (entry.entry_type === 'social_media_visit') {
        return `Visited ${entry.entry_value}`;
    }
    return entry.entry_value;
};

const hasEntry = (type, value) => {
    return entries.value.some(entry =>
        entry.entry_type === type &&
        entry.entry_value.toLowerCase() === value.toLowerCase()
    );
};

const registerSocialMediaVisit = async (profile, type) => {
    try {
        const response = await axios.post(route('giveaway.participant.extend.entry', props.uuid), {
            entry_type: type,
            entry_value: profile.name,
            profile_url: profile.url
        });

        if (response.data.success) {
            fetchCompetition();
            open(profile.url, '_blank');
        }
    } catch (error) {
        console.error('Error registering visit:', error);
    }
};

const registerBasicAccount = async () => {
    try {
        const response = await axios.post(route('giveaway.participant.extend.entry', props.uuid), {
            entry_type: 'registration',
            entry_value: 'myflowBasicPending'
        });

        if (response.data.success) {
            fetchCompetition();
            window.open(route('giveaway.participant.extend.basic', props.uuid), '_blank');
        }
    } catch (error) {
        console.error('Error registering basic account:', error);
    }
};

const calculatePrizeValue = (prize) => {
    const proMonthsValue = prize.proMonths * PRO_MONTH_VALUE;
    const benefitsValue = prize.benefits.reduce((total, benefit) => {
        return total + (Number(benefit.value) || 0);
    }, 0);
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(proMonthsValue + benefitsValue);
};

const maxPossibleEntries = computed(() => {
    return 6 + competition.value?.social_media_profiles.length
})

onMounted(() => {
    fetchCompetition();
});
</script>

<template>
    <div class="flex flex-col min-h-screen bg-gradient-to-b from-gray-100 to-gray-300">
        <div class="flex-grow">
            <div class="max-w-2xl px-4 py-12 mx-auto sm:px-6 lg:px-8">
                <div class="bg-white shadow-xl rounded-wl">
                    <div class="flex justify-center font-bold text-center bg-wlPrimary rounded-t-wl text-wlPrimaryContrast">
                        <div class="p-4">
                            <date-countdown
                                v-if="competition && ! competition.has_ended"
                                :date-time="competition.end_date"
                                suffix=" left"
                            ></date-countdown>
                        </div>
                    </div>

                    <div>
                        <div
                            v-if="competition"
                        >
                            <div
                                v-if="competition && participant"
                                class="px-4 py-6"
                            >

                                <div class="flex items-center justify-center text-center" v-if="entries.length > 0">
                                    <div>
                                        <span class="text-xl font-bold ">You've claimed {{ entries.length }} of {{ maxPossibleEntries }} possible entries</span>
                                        <p
                                            v-if="! competition.has_ended"
                                            class="text-lg"
                                        >
                                            The more entries you have, the higher your chances of winning!
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="competition.has_ended"
                                class="mb-4 text-gray-400"
                            >
                                <p class="text-lg text-center">This competition has ended</p>
                            </div>
                            <div v-if="! competition.has_ended">
                                <p class="mb-4 text-center text-gray-600">Complete any of the following actions to earn more entries:</p>

                                <div class="mt-8 mb-6 divide-y">
                                    <div
                                        v-for="(profile, index) in competition.social_media_profiles"
                                        :key="index"
                                        class="px-4 py-2 bg-gray-100 first:border-t"
                                    >
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center">
                                                <div
                                                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-3 text-3xl text-wlPrimary"
                                                >
                                                    <i :class="[profile.icon]"></i>
                                                </div>
                                                <a class="block" style="text-decoration: none;" :href="profile.url" target="_blank" rel="noopener noreferrer">
                                                    <div class="text-base font-bold">{{ profile.name }}</div>
                                                    <div class="text-sm text-gray-500">Follow page</div>
                                                </a>
                                            </div>
                                            <button-base
                                                v-if="!hasEntry('social_media_visit', profile.name)"
                                                style-type="primary"
                                                @click="registerSocialMediaVisit(profile, 'social_media_visit')"
                                                class="justify-center w-28"
                                            >
                                                <template #icon>
                                                    <i class="mr-1 fa-light fa-plus"></i>
                                                </template>
                                                1 Entry
                                            </button-base>
                                            <span v-else
                                                class="px-3 py-1 text-base font-medium text-green-700 bg-green-100 rounded-full">
                                                Completed
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        class="py-2 px-4 bg-gray-100 !border-b"
                                    >
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center">
                                                <div
                                                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-3 text-3xl text-wlPrimary"
                                                >
                                                    <logo-icon class="mr-1 w-7 h-7 text-wlPrimary"></logo-icon>
                                                </div>
                                                <div>
                                                    <div class="text-base font-bold">myflow Basic</div>
                                                    <div class="text-sm text-gray-500">Register a <b>free forever</b> myflow Basic account</div>
                                                </div>
                                            </div>
                                            <button-base
                                                v-if="!hasEntry('registration', 'myflowBasicPending') && !hasEntry('registration', 'myflowBasic')"
                                                style-type="primary"
                                                @click="registerBasicAccount()"
                                                class="justify-center w-28"
                                            >
                                                <template #icon>
                                                    <i class="mr-1 fa-light fa-plus"></i>
                                                </template>
                                                5 Entries
                                            </button-base>

                                            <span v-if="hasEntry('registration', 'myflowBasic') && !hasEntry('registration', 'myflowBasicPending')"
                                                class="px-3 py-1 text-base font-medium text-green-700 bg-green-100 rounded-full">
                                                Completed
                                            </span>

                                            <span v-if="hasEntry('registration', 'myflowBasicPending') && !hasEntry('registration', 'myflowBasic')"
                                                class="px-3 py-1 text-base font-medium text-yellow-700 bg-yellow-100 rounded-full">
                                                Pending
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="p-4">
                            <h2 class="mb-2 text-2xl font-bold text-center text-gray-800">Prizes</h2>
                            <div class="divide-y">
                                <div
                                    v-for="(prize, index) in competition?.prizes"
                                    :key="index"
                                    class="px-6 py-2 -mx-4"
                                >
                                    <div>
                                        <div class="flex items-center justify-between mb-4">
                                            <h3 class="text-lg font-medium text-wlPrimary">Prize {{ index + 1 }}</h3>
                                            <pill class="text-black bg-gray-100">
                                                {{ prize.winners }} {{ prize.winners > 1 ? 'winners' : 'winner' }}
                                            </pill>
                                        </div>
                                        <div class="flex justify-between">
                                            <ul class="space-y-2 text-sm">
                                                <li class="flex items-center">
                                                    <logo-icon class="w-5 h-5 mr-1 text-wlPrimary"></logo-icon>
                                                    {{ prize.proMonths }} myflow Pro month{{ prize.proMonths > 1 ? 's' : '' }}
                                                </li>


                                                <li v-for="(benefit, bIndex) in prize.benefits" :key="bIndex"
                                                    class="flex items-center">
                                                    <svg class="w-5 h-5 mr-2 text-wlPrimary" fill="currentColor"
                                                         viewBox="0 0 20 20">
                                                        <path fill-rule="evenodd"
                                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                              clip-rule="evenodd" />
                                                    </svg>
                                                    {{ benefit.description }}
                                                </li>
                                            </ul>
                                            <div>
                                                <div class="text-center">
                                                    <div class="text-gray-400">Total Value</div>
                                                    <div class="text-xl font-semibold">
                                                        €{{ calculatePrizeValue(prize) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <footer class="py-6 bg-gray-800">
            <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <p class="text-base text-center text-gray-400">
                    &copy; {{ new Date().getFullYear() }} Giveaway powered by myflow
                </p>
            </div>
        </footer>
    </div>
</template>
